/**
 * "Send this list on a schedule" (#1432, Cycle 3 C3-6, D8).
 *
 * Built to threads/cycle-3-report-library/mockup.html, panel 2. A deviation
 * from the mockup is a bug (docs/bible/design-to-code-handoff.md).
 *
 * Three things this dialog refuses to do, all on purpose:
 *
 *  • NO CRON STRING. How often is a choice of three, the day is a dropdown,
 *    and the time is a whole hour on the office clock. "0 7 * * 1" is a thing
 *    that gets typed wrong once and sends at the wrong hour for a year.
 *  • NO FREE-TEXT ADDRESS. Recipients come from the office list the server
 *    hands back, so a customer address cannot be typed in here at all.
 *  • NO PROMISE IT CANNOT KEEP. The "First send" line runs the real list and
 *    reports the real row count, because a promised number that turns out
 *    wrong on Monday is worse than no number.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { COMPLIANCE_COPY as C } from "@/pages/compliance/compliance-copy";

const D = C.section.dialog;

export type Cadence = "daily" | "weekly" | "monthly";
export type ScheduleFormat = "csv" | "pdf";

export interface SavedViewSchedule {
    id: string;
    savedViewId: string;
    viewName: string | null;
    surface: string | null;
    cadence: Cadence;
    dayOfWeek: number | null;
    dayOfMonth: number | null;
    hourUtc: number;
    recipientUserIds: string[];
    format: ScheduleFormat;
    skipWhenEmpty: boolean;
    isActive: boolean;
    lastRunAt: string | null;
    lastStatus: "sent" | "skipped_empty" | "failed" | null;
    lastRowCount: number | null;
    description: string;
    nextSendAt: string | null;
    lastSendDate: string | null;
}

interface Recipient {
    id: string;
    name: string;
    email: string;
}

/**
 * The office clock. Hours are stored UTC — every other cron in this codebase
 * ticks on a fixed UTC hour and drifts an hour across daylight saving rather
 * than skipping or double-firing a day — and rendered Central here, because a
 * screen that says 13:00 when the email lands at 8 is a screen people ignore.
 */
const CENTRAL_OFFSET = -5;
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function centralHourOf(hourUtc: number): number {
    return (((hourUtc + CENTRAL_OFFSET) % 24) + 24) % 24;
}

export function utcHourOf(centralHour: number): number {
    return (((centralHour - CENTRAL_OFFSET) % 24) + 24) % 24;
}

export function centralLabel(centralHour: number): string {
    const suffix = centralHour < 12 ? "AM" : "PM";
    const twelve = centralHour % 12 === 0 ? 12 : centralHour % 12;
    return `${twelve}:00 ${suffix} Central`;
}

/** 5 AM to 8 PM. Nobody schedules a report for 3 in the morning on purpose. */
const HOUR_CHOICES = Array.from({ length: 16 }, (_, i) => i + 5);

function ordinal(n: number): string {
    const rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1:
            return `${n}st`;
        case 2:
            return `${n}nd`;
        case 3:
            return `${n}rd`;
        default:
            return `${n}th`;
    }
}

/** "Monday 14 Sep 2026, 7:00 AM Central" — the First send line's left half. */
export function firstSendLabel(iso: string | null): string {
    if (!iso) return C.section.schedules.none;
    const d = new Date(iso);
    const day = d.toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
    return `${day}, ${centralLabel(centralHourOf(d.getUTCHours()))}`;
}

const selectCls = "flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

interface Props {
    open: boolean;
    onClose: () => void;
    savedViewId: string;
    savedViewName: string;
    /** Editing an existing schedule, or null to set a new one up. */
    existing?: SavedViewSchedule | null;
}

export function ScheduleDialog({ open, onClose, savedViewId, savedViewName, existing }: Props) {
    const qc = useQueryClient();
    const [cadence, setCadence] = useState<Cadence>("weekly");
    const [dayOfWeek, setDayOfWeek] = useState(1);
    const [dayOfMonth, setDayOfMonth] = useState(1);
    const [centralHour, setCentralHour] = useState(7);
    const [format, setFormat] = useState<ScheduleFormat>("csv");
    const [recipients, setRecipients] = useState<string[]>([]);
    const [skipWhenEmpty, setSkipWhenEmpty] = useState(true);

    // Reopening the dialog on a different view must not inherit the last one's
    // answers — that is how somebody's Monday list quietly becomes daily.
    useEffect(() => {
        if (!open) return;
        setCadence(existing?.cadence ?? "weekly");
        setDayOfWeek(existing?.dayOfWeek ?? 1);
        setDayOfMonth(existing?.dayOfMonth ?? 1);
        setCentralHour(centralHourOf(existing?.hourUtc ?? utcHourOf(7)));
        setFormat(existing?.format ?? "csv");
        setRecipients(existing?.recipientUserIds ?? []);
        setSkipWhenEmpty(existing?.skipWhenEmpty ?? true);
    }, [open, existing]);

    const peopleQ = useQuery({
        queryKey: ["saved-view-schedules", "recipients"],
        queryFn: () => api.get<{ recipients: Recipient[] }>("/api/saved-view-schedules/recipients"),
        enabled: open,
        staleTime: 300_000,
    });
    const people = peopleQ.data?.recipients ?? [];

    const previewQ = useQuery({
        queryKey: ["saved-view-schedules", "preview", savedViewId],
        queryFn: () => api.post<{ rowCount: number }>("/api/saved-view-schedules/preview", { savedViewId }),
        enabled: open && Boolean(savedViewId),
        retry: false,
        staleTime: 60_000,
    });

    const hourUtc = utcHourOf(centralHour);

    /** What the server will compute, computed here so the line is instant. */
    const firstSendIso = useMemo(() => {
        const now = new Date();
        for (let i = 0; i <= 400; i++) {
            const when = new Date(
                Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + i, hourUtc, 0, 0, 0),
            );
            if (when.getTime() < now.getTime()) continue;
            if (cadence === "daily") return when.toISOString();
            if (cadence === "weekly" && when.getUTCDay() === dayOfWeek) return when.toISOString();
            if (cadence === "monthly" && when.getUTCDate() === dayOfMonth) return when.toISOString();
        }
        return null;
    }, [cadence, dayOfWeek, dayOfMonth, hourUtc]);

    const save = useMutation({
        mutationFn: async () => {
            const body = {
                cadence,
                dayOfWeek: cadence === "weekly" ? dayOfWeek : null,
                dayOfMonth: cadence === "monthly" ? dayOfMonth : null,
                hourUtc,
                recipientUserIds: recipients,
                format,
                skipWhenEmpty,
            };
            if (existing) return api.patch(`/api/saved-view-schedules/${existing.id}`, body);
            return api.post("/api/saved-view-schedules", { ...body, savedViewId });
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["saved-view-schedules"] });
            toast.success(existing ? D.saved : D.started);
            onClose();
        },
        onError: (e: unknown) => {
            toast.error(e instanceof Error && e.message ? e.message : C.section.schedules.saveFailed);
        },
    });

    function toggle(id: string) {
        setRecipients((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    }

    const chosen = people.filter((p) => recipients.includes(p.id));
    const unchosen = people.filter((p) => !recipients.includes(p.id));

    return (
        <Dialog open={open} onClose={onClose} title={D.title(savedViewName)}>
            <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium" htmlFor="sched-cadence">
                            {D.howOften}
                        </label>
                        <select
                            id="sched-cadence"
                            className={selectCls}
                            value={cadence}
                            onChange={(e) => setCadence(e.target.value as Cadence)}
                        >
                            <option value="daily">{D.daily}</option>
                            <option value="weekly">{D.weekly}</option>
                            <option value="monthly">{D.monthly}</option>
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-medium" htmlFor="sched-day">
                            {D.whichDay}
                        </label>
                        {cadence === "weekly" ? (
                            <select
                                id="sched-day"
                                className={selectCls}
                                value={dayOfWeek}
                                onChange={(e) => setDayOfWeek(Number(e.target.value))}
                            >
                                {DAY_NAMES.map((d, i) => (
                                    <option key={d} value={i}>
                                        {d}
                                    </option>
                                ))}
                            </select>
                        ) : cadence === "monthly" ? (
                            <select
                                id="sched-day"
                                className={selectCls}
                                value={dayOfMonth}
                                onChange={(e) => setDayOfMonth(Number(e.target.value))}
                            >
                                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                                    <option key={d} value={d}>
                                        {ordinal(d)}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className={`${selectCls} items-center text-muted-foreground`}>{D.daily}</div>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-medium" htmlFor="sched-hour">
                            {D.whatTime}
                        </label>
                        <select
                            id="sched-hour"
                            className={selectCls}
                            value={centralHour}
                            onChange={(e) => setCentralHour(Number(e.target.value))}
                        >
                            {HOUR_CHOICES.map((h) => (
                                <option key={h} value={h}>
                                    {centralLabel(h)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-medium" htmlFor="sched-format">
                            {D.asLabel}
                        </label>
                        <select
                            id="sched-format"
                            className={selectCls}
                            value={format}
                            onChange={(e) => setFormat(e.target.value as ScheduleFormat)}
                        >
                            <option value="csv">{D.csv}</option>
                            <option value="pdf">{D.pdf}</option>
                        </select>
                    </div>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-medium">{D.sendTo}</label>
                    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 text-sm">
                        {chosen.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => toggle(p.id)}
                                className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5"
                                title={p.email}
                            >
                                {p.name}
                                <X className="h-3 w-3 text-foreground/60" />
                            </button>
                        ))}
                        {unchosen.length > 0 ? (
                            <select
                                className="min-w-[140px] flex-1 bg-transparent text-sm outline-none"
                                value=""
                                aria-label={D.sendTo}
                                onChange={(e) => e.target.value && toggle(e.target.value)}
                            >
                                <option value="">{chosen.length === 0 ? D.sendTo : "+"}</option>
                                {unchosen.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            people.length === 0 && <span className="text-foreground/60">{D.sendToEmpty}</span>
                        )}
                    </div>
                    <p className="text-[11px] text-foreground/60">{D.sendToNote}</p>
                </div>

                <label className="flex items-start gap-2 text-sm">
                    <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4"
                        checked={skipWhenEmpty}
                        onChange={(e) => setSkipWhenEmpty(e.target.checked)}
                    />
                    <span>
                        {D.skipEmpty}
                        <span className="block text-[11px] text-foreground/60">{D.skipEmptyWhy}</span>
                    </span>
                </label>

                <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground/70">
                        {D.firstSend}
                    </div>
                    <div className="mt-0.5">
                        {previewQ.isPending
                            ? D.firstSendCounting
                            : previewQ.isError
                              ? D.firstSendNone
                              : D.firstSendLine(firstSendLabel(firstSendIso), String(previewQ.data?.rowCount ?? 0))}
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={save.isPending}>
                        {D.cancel}
                    </Button>
                    <Button
                        onClick={() => (recipients.length === 0 ? toast.error(D.pickOne) : save.mutate())}
                        disabled={save.isPending}
                    >
                        {existing ? D.save : D.start}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
